import React, { FC } from 'react';
import { useHistory } from 'react-router';
import { yupResolver } from '@hookform/resolvers/yup';
import { FormProvider, useForm } from 'react-hook-form';
import { ROUTE_SUPPLIER_INFO } from 'constant/routes';
import { SupplierCreateFormT } from 'domain/entity/supplier/SupplierCreateFormData';
import { Supplier } from 'domain/entity/supplier/SuplierData';
import { supplierSlice } from 'data/store/supplierReducer';
import { confirmationSlice } from 'data/store/confirmationReducer';
import useGlobalTranslation from 'hooks/useGlobalTranlation';
import { useAppDispatch, useAppSelector } from 'hooks/redux';
import { getErrMsg } from 'helpers/locale';
import schema from './validation';
import Icon from 'components/common/Icon';
import { Button, Input } from 'components/ui';
import SimplePage from 'components/common/SimplePage';
import DateField from 'components/controls/DateField';
import backIcon from 'components/svg/arrow-left-light.svg?sprite';
import { SUPPLIER_INFO_FIELDS } from './fields';
import {
  backIconCss,
  StyledLink,
  StyledLinkText,
  StyledSubmitWrapper
} from '../../../../Landlord/components/LandlordEdit/styled';
import { StyledForm, StyledFormContainer } from './styled';

const SupplierInfoEdit: FC = () => {
  const { g } = useGlobalTranslation();
  const dispatch = useAppDispatch();
  const { setConfirmationData, resetConfirmationData } = confirmationSlice.actions;
  const { SupplierUpdate } = supplierSlice.actions;
  const history = useHistory();
  const { currentSupplier, pending, error } = useAppSelector(({ supplier }) => supplier);
  const form = useForm<SupplierCreateFormT>({
    resolver: yupResolver(schema),
    defaultValues: {
      name: (currentSupplier && currentSupplier.name) || '',
      address: (currentSupplier && currentSupplier.address) || '',
      postcode: (currentSupplier && currentSupplier.postcode) || '',
      archived: currentSupplier && currentSupplier.archived,
      city: (currentSupplier && currentSupplier.city) || ''
    }
  });
  const { register, handleSubmit, formState, getValues } = form;
  const { name } = currentSupplier || {};

  const onSubmit = (data: Supplier) => {
    dispatch(
      setConfirmationData({
        show: true,
        cancelButtonProps: {
          onClick: () => {
            dispatch(resetConfirmationData());
          },
          children: g('cancel')
        },
        confirmButtonProps: {
          onClick: () => {
            dispatch(SupplierUpdate({ ...data, id: (currentSupplier && currentSupplier.id) || 0 }));
            dispatch(resetConfirmationData());
            history.goBack();
          },
          children: g('save')
        },
        text: g('alerts.saveChangesSupplier.message'),
        title: g('alerts.saveChangesSupplier.title')
      })
    );
  };

  return (
    <SimplePage
      header={
        <StyledLink to={ROUTE_SUPPLIER_INFO}>
          <Icon icon={backIcon} css={backIconCss} color={'black01'} />
          <StyledLinkText>{name}</StyledLinkText>
        </StyledLink>
      }
    >
      <StyledForm onSubmit={handleSubmit(onSubmit)}>
        <StyledFormContainer>
          {SUPPLIER_INFO_FIELDS.map(({ name, text, serverError, type, ...restProps }, index) => (
            <FormProvider {...form}>
              {type !== 'date' && (
                <Input
                  variant={'outline'}
                  maxLength={50}
                  key={name}
                  {...restProps}
                  required
                  label={g('inputs.' + text)}
                  register={register(name)}
                  error={formState.errors[name]}
                  errorMessage={getErrMsg(formState.errors, name)}
                  serverError={(serverError && g(formState.errors[name] || '')) || undefined}
                />
              )}
              {type === 'date' && (
                <DateField
                  variant={'outline'}
                  key={name}
                  type={'date'}
                  value={getValues(name)}
                  label={g('inputs.' + text)}
                  register={register(name)}
                  error={formState.errors[name]}
                  errorMessage={getErrMsg(formState.errors, name)}
                  serverError={(serverError && g(formState.errors[name] || '')) || undefined}
                />
              )}
            </FormProvider>
          ))}
        </StyledFormContainer>
        <StyledSubmitWrapper>
          <Button isLoading={pending}>{g('save')}</Button>
        </StyledSubmitWrapper>
      </StyledForm>
    </SimplePage>
  );
};

export default SupplierInfoEdit;
